import React, { useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Button,
  Grid,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { dspState } from '../../../atoms/DspState';
import { toggleEditWarehouse } from '../../../atoms/ToggleEditWarehouse';
import { warehouseObjState } from '../../../atoms/WarehouseObjState';
import { getGeocode, listClient, updateWarehouse } from '../../../repositories/HttpRepository';
import * as Constants from '../constants';
import InputField from '../../../components/InputField/input';
import Select from '../../../components/SelectField/select';

import GeocodeResult from './../AddWarehouse/GeocodeResult';
import * as Styles from './../AddWarehouse/styles';
import { AxlButton } from 'components/AxlButton';

function EditWarehouse() {
  const [warehouseEditData, setWarehouseEditData] = useRecoilState(warehouseObjState);
  const [warehouse, setWarehouse] = useState(warehouseEditData);
  const [enableAddBtn, setEnableAddBtn] = useState(false);
  const [changeGeocode, setChangeGeocode] = useState(false);
  const [geoCodeStatus, setGeoCodeStatus] = useState(false);
  const [geoCodeInfos, setGeoCodeInfos] = useState({});
  const setTurnOnEditWarehouse = useSetRecoilState(toggleEditWarehouse);
  const [displayGeocodeResult, setDisplayGeocodeResult] = useState(false);
  const [disableLatLng, setDisableLatLng] = useState(true);
  const [changeData, setChangeData] = useState(false);

  const navigate = useNavigate();
  const { classes } = Styles.useStyles();

  let defaultWarehouseTypeValue;
  let defaultTypeValue;

  const [typeOptions, setTypeOptions] = useState([]);

  const [dspList, setDspList] = useRecoilState(dspState);

  const dspOptions = useMemo(
    () =>
      dspList?.items?.map((dsp) => ({
        title: dsp.code ?? dsp.company,
        value: dsp.id,
        label: dsp.code ?? dsp.company,
      })),
    [dspList],
  );

  switch (warehouse.owner_type) {
    case Constants.OWNER_TYPE_CLIENT_STR:
      defaultWarehouseTypeValue = Constants.OWNER_TYPE_CLIENT;
      defaultTypeValue = typeOptions?.filter((type) => type.value === warehouse.owner_id)[0] ?? {};
      break;
    case Constants.OWNER_TYPE_COURIER_STR:
      defaultWarehouseTypeValue = Constants.OWNER_TYPE_COURIER;
      defaultTypeValue = typeOptions?.filter((type) => type.value === warehouse.owner_id)[0] ?? {};
      break;
    default:
      defaultWarehouseTypeValue = Constants.OWNER_TYPE_JITSU;
      defaultTypeValue = typeOptions?.filter((type) => type.value === warehouse.owner_type)[0] ?? {};
      break;
  }

  const [selectedWarehouseType, setSelectedWarehouseType] = useState(defaultWarehouseTypeValue);

  useEffect(() => {
    if (defaultWarehouseTypeValue === Constants.OWNER_TYPE_JITSU) {
      // out WH
      setTypeOptions(Constants.defaultTypeOptions());
    } else if (defaultWarehouseTypeValue === Constants.OWNER_TYPE_CLIENT) {
      listClient({ size: 1000000 }).then((resp) => {
        if (resp.ok) {
          let items = resp.data.items;
          let newTypes = [];
          let i = 0;
          for (i = 0; i < items.length; i++) {
            newTypes[i + 1] = { value: items[i].id, title: items[i].name };
          }
          setTypeOptions(newTypes);
        }
      });
    } else if (defaultWarehouseTypeValue === Constants.OWNER_TYPE_COURIER) {
      setTypeOptions(dspOptions);
    }
  }, []);

  // // Check form can submit or not
  const checkSubmit = () => {
    return (
      changeData &&
      warehouse.alias &&
      warehouse.owner_type &&
      warehouse.address.street &&
      warehouse.address.city &&
      warehouse.address.state &&
      warehouse.address.zipcode &&
      warehouse.address.lat &&
      warehouse.address.lng
    );
  };

  useEffect(() => {
    if (checkSubmit()) {
      setEnableAddBtn(true);
    } else {
      setEnableAddBtn(false);
    }
  }, [warehouse]);

  function submitForm(e) {
    e.preventDefault();

    if (changeGeocode) {
      toast.error('You just change your address, pls geocode it before the update');
      return;
    }

    if (!checkSubmit()) {
      toast.error('Please fill all require fields!');
      return;
    }

    let tmp = { ...warehouse };
    delete tmp.updated_by_str;
    delete tmp.client_name;
    updateWarehouse(tmp, warehouse.id).then((res) => {
      if (res.ok) {
        setWarehouseEditData(warehouse);
        toast.success('updated successfully !');
        setTurnOnEditWarehouse(false);
      } else {
        toast.error('Server is having problem, please try again later.');
      }
    });
  }

  function handeChangeWarehouseType(e) {
    // set owner types
    // no-dd-sa:javascript-code-style/assignment-name
    let warehouse_type_selected = e.target.value;
    setSelectedWarehouseType(warehouse_type_selected);
    if (warehouse_type_selected === 1) {
      // our WH
      setTypeOptions(Constants.defaultTypeOptions());
    } else if (warehouse_type_selected === 2) {
      listClient({ size: 1000000 }).then((resp) => {
        if (resp.ok) {
          let items = resp.data.items;
          let newTypes = [];
          let i = 0;
          for (i = 0; i < items.length; i++) {
            newTypes[i + 1] = { value: items[i].id, title: items[i].name };
          }
          setTypeOptions(newTypes);
        }
      });
    } else if (warehouse_type_selected === 3) {
      setTypeOptions(dspOptions);
    }
    setChangeData(true);
  }

  function handleOwnerTypeChange(value) {
    if (selectedWarehouseType === Constants.OWNER_TYPE_JITSU) {
      setWarehouse({
        ...warehouse,
        owner_id: null,
        owner_type: value, // Main or Mobile Hub
      });
    } else if (selectedWarehouseType === Constants.OWNER_TYPE_CLIENT) {
      setWarehouse({
        ...warehouse,
        owner_id: value,
        owner_type: Constants.OWNER_TYPE_CLIENT_STR,
      });
    } else if (selectedWarehouseType === Constants.OWNER_TYPE_COURIER) {
      setWarehouse({
        ...warehouse,
        owner_id: value,
        owner_type: Constants.OWNER_TYPE_COURIER_STR,
      });
    }
    setChangeData(true);
  }

  function handleCancel(e) {
    setTurnOnEditWarehouse(false);
    navigate('/warehouses/' + warehouse.id);
  }

  function handleOnClickGeocode() {
    // Set latitude & longitude
    const { address } = warehouse;

    if (address.street && address.city && address.state && address.zipcode) {
      getGeocode(warehouse.address).then((resp) => {
        let lat = warehouse.address.lat;
        let lng = warehouse.address.lng;

        if (resp.ok && resp.data.latitude && resp.data.longitude) {
          lat = resp.data.latitude;
          lng = resp.data.longitude;
          setDisableLatLng(true);
          setGeoCodeStatus(true);
        } else {
          setDisableLatLng(false);
          setGeoCodeStatus(false);
        }
        setChangeGeocode(false);
        setWarehouse({
          ...warehouse,
          address: {
            ...warehouse.address,
            lat: lat,
            lng: lng,
          },
        });
        setGeoCodeInfos(warehouse);
        setDisplayGeocodeResult(true);
      });
    } else {
      toast.error('Please fill all the addresses information!');
    }
  }

  function handleOnChangeInput(e) {
    const key = e.target.getAttribute('id').trim();
    const value = e.target.value;

    let tmp = { ...warehouse };
    tmp[key] = value;
    setWarehouse(tmp);
    setChangeData(true);
  }

  function validateLatLng(data) {
    const lat = isNaN(parseFloat(data.lat)) ? 0 : parseFloat(data.lat);
    const lng = isNaN(parseFloat(data.lng)) ? 0 : parseFloat(data.lng);
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  function handleOnChangeInputAddress(e) {
    const key = e.target.getAttribute('id').trim();
    let value = e.target.value;

    if (key !== 'lat' && key !== 'lng') {
      setChangeGeocode(true);
    }
    let tmp = { ...warehouse.address };
    tmp[key] = value;
    if (key === 'lat' || key === 'lng') {
      if (!validateLatLng(tmp)) {
        toast.error('Invalid latitude or longitude');
        return;
      }
    }

    setWarehouse({ ...warehouse, address: tmp });

    if (geoCodeStatus === false && tmp.lat && tmp.lng) {
      setDisplayGeocodeResult(false);
    }

    setChangeData(true);
  }

  return (
    <Dialog
      open={true}
      onClose={() => {
        setTurnOnEditWarehouse(false);
      }}
      aria-labelledby="form-dialog-title"
      maxWidth="md"
    >
      <DialogTitle id="form-dialog-title" className={classes.Dialog}>
        <Typography variant="h4">Edit warehouse</Typography>
        <CloseIcon className={classes.closeBtn} onClick={handleCancel} />
      </DialogTitle>
      <DialogContent className={classes.formAdd}>
        {!typeOptions || typeOptions.length === 0 ? (
          <div
            style={{
              height: 320,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 960,
            }}
          >
            <CircularProgress size={28} />
          </div>
        ) : (
          <form className={classes.root} noValidate autoComplete="off">
            <Grid container spacing={3}>
              <InputField
                label="Warehouse Alias"
                column="12"
                id="alias"
                value={warehouse.alias}
                onChange={handleOnChangeInput}
                required
              />
              <Select
                label="Warehouse Type"
                column="6"
                id="warehouse-type"
                options={Constants.defaultWarehouseTypeOptions}
                value={defaultWarehouseTypeValue}
                onChange={handeChangeWarehouseType}
              />
              {/* <Select label="Owner Type" column="6" id="owner-type" options={typeOptions} value={defaultTypeValue} onChange={handleOwnerTypeChange}/> */}
              <Grid size={6}>
                <Autocomplete
                  defaultValue={defaultTypeValue}
                  options={typeOptions}
                  renderInput={(params) => (
                    <>
                      <span>Owner Type</span>
                      <TextField {...params} variant="outlined" size="small" fullWidth />
                    </>
                  )}
                  getOptionLabel={(option) => `[${option.value}] - ${option.title}`}
                  onChange={(e, values) => handleOwnerTypeChange(values?.value)}
                />
              </Grid>
              <InputField
                label="Address Line 1"
                column="12"
                id="street"
                value={warehouse.address.street}
                onChange={handleOnChangeInputAddress}
                required
              />
              <InputField
                label="Address Line 2 (optional)"
                column="12"
                id="street2"
                value={warehouse.address.street2}
                onChange={handleOnChangeInputAddress}
              />
              <InputField
                label="City"
                column="4"
                id="city"
                value={warehouse.address.city}
                onChange={handleOnChangeInputAddress}
              />
              <InputField
                label="State"
                column="4"
                id="state"
                value={warehouse.address.state}
                onChange={handleOnChangeInputAddress}
              />
              <InputField
                label="Zipcode"
                column="4"
                id="zipcode"
                value={warehouse.address.zipcode}
                onChange={handleOnChangeInputAddress}
              />
              <InputField
                label="Latitude"
                column="5"
                id="lat"
                disabled={disableLatLng}
                value={warehouse.address.lat}
                onChange={handleOnChangeInputAddress}
              />
              <InputField
                label="Longitude"
                column="5"
                id="lng"
                disabled={disableLatLng}
                value={warehouse.address.lng}
                onChange={handleOnChangeInputAddress}
              />
              <Grid size={2}>
                <Button variant="contained" size="large" onClick={handleOnClickGeocode} className={classes.GeoCode}>
                  GEOCODE
                </Button>
              </Grid>
              <Grid size={12}>
                {displayGeocodeResult && <GeocodeResult data={geoCodeInfos} status={geoCodeStatus} />}
              </Grid>
              <InputField
                label="Access code"
                column="12"
                id="access_code"
                value={warehouse.access_code}
                onChange={handleOnChangeInput}
              />
              <Grid size={12}>
                <span>Pickup Instructions</span>
                <TextField
                  id="pickup_note"
                  variant="outlined"
                  fullWidth
                  size="small"
                  placeholder="Add pickup note/instructions here…"
                  onChange={handleOnChangeInput}
                  multiline
                  rows={3}
                  value={warehouse.pickup_note}
                />
              </Grid>

              <Grid size={12}>
                <span>Return Instructions</span>
                <TextField
                  id="return_note"
                  variant="outlined"
                  fullWidth
                  size="small"
                  placeholder="Add return note/instructions here…"
                  onChange={handleOnChangeInput}
                  multiline
                  rows={3}
                  value={warehouse.return_note}
                  slotProps={{
                    htmlInput: { maxLength: 1024 },
                  }}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogAction}>
        <AxlButton onClick={handleCancel} variant="contained" isCancel>
          CANCEL
        </AxlButton>
        <AxlButton onClick={submitForm} variant="contained">
          SAVE
        </AxlButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditWarehouse;
